/**
 * Drawings of an aircraft type and the pin-drift rule, shared by the API and
 * the demo store so both answer a replaced drawing or a changed type the same
 * way (docs/fleet-admin-ui.md, section 4; CLAUDE.md rule 27). Pure.
 *
 * The three base views (oben, vorne, links) are what a type needs; until all
 * three exist the type is shown with the placeholder silhouette, every view
 * alike. A derived view (unten, rechts, hinten) may get its own image; it then
 * leaves the type's mirror rules. The cabin view (innen) is optional and has
 * no mirror: without its own image it is the cabin placeholder, at
 * CABIN_PLACEHOLDER_SIZE, so its pins are normalised to that until a real
 * interior drawing replaces it.
 *
 * Pins are stored normalised to the image the view is drawn from (spec 7), so
 * a new image keeps every pin at the same relative position — which is the
 * same place on the aircraft only if the new image has the same proportions.
 * `drift` compares the effective size of every view that carries damages
 * before and after a change; beyond 2 % the change needs `force`.
 */
import { type AircraftView, type BaseView, BASE_VIEWS, CABIN_VIEW, resolveView } from './geometry';

/**
 * The views a damage can be recorded on and a drawing can be given for.
 * „hinten“ is gone (owner, 09.10.2026): it was the mirrored front view,
 * propeller and all, the tail narrows to a point, and no club had recorded
 * anything there. The value stays in AircraftView and in the database CHECK
 * only so old rows and mirror rules still parse.
 */
export const ALL_VIEWS: readonly AircraftView[] = [
  'oben',
  'unten',
  'links',
  'rechts',
  'vorne',
  'innen',
];
export const DERIVED_VIEWS = ['unten', 'rechts'] as const;

/** What a type without its three base drawings is measured against (and what pins on it were recorded against). */
export const PLACEHOLDER_SIZE = { width: 1500, height: 1000 } as const;

/** The built-in cabin schematic (innen, nose up): what interior pins are recorded against without an own image. */
export const CABIN_PLACEHOLDER_SIZE = { width: 1000, height: 1500 } as const;

/** The size a view is drawn at while it has no real drawing. */
export function placeholderSizeOf(view: AircraftView): { width: number; height: number } {
  return view === CABIN_VIEW ? { ...CABIN_PLACEHOLDER_SIZE } : { ...PLACEHOLDER_SIZE };
}

/** Relative change of height/width beyond which pins would visibly drift. */
export const ASPECT_TOLERANCE = 0.02;

/** 409 code of a change that would move pins: the client offers "Trotzdem übernehmen" (force). */
export const PINS_DRIFT = 'pins_drift';

export type MirrorRules = Partial<Record<AircraftView, BaseView>>;

export function isAircraftView(value: unknown): value is AircraftView {
  return typeof value === 'string' && (ALL_VIEWS as readonly string[]).includes(value);
}

/** All three base views present: the type leaves the placeholder. */
export function isComplete(drawings: Partial<Record<AircraftView, unknown>>): boolean {
  return BASE_VIEWS.every((v) => drawings[v] !== undefined);
}

/**
 * The drawing set and mirror rules after storing `entry` for `view`. A
 * derived view with its own image is no longer mirrored (spec 7: "Spiegelregel
 * pro Muster …, damit Ausnahmen möglich sind").
 */
export function withDrawing<E>(
  drawings: Partial<Record<AircraftView, E>>,
  rules: MirrorRules,
  view: AircraftView,
  entry: E,
): { drawings: Partial<Record<AircraftView, E>>; rules: MirrorRules } {
  const nextRules: MirrorRules = { ...rules };
  if (!(BASE_VIEWS as readonly string[]).includes(view)) delete nextRules[view];
  return { drawings: { ...drawings, [view]: entry }, rules: nextRules };
}

/** How the size of one view's image is determined; the caller supplies it per type kind. */
export type SizeOf = (view: AircraftView) => { width: number; height: number };

/**
 * The size a view is actually drawn at: the image it resolves to through the
 * mirror rules. `sizeOf` answers for an image view (base or own derived).
 */
export function effectiveSize(
  view: AircraftView,
  rules: MirrorRules,
  sizeOf: SizeOf,
): { width: number; height: number } {
  return sizeOf(resolveView(view, rules).base);
}

/** |Δ(height/width)| relative to the old ratio. */
export function aspectChange(
  before: { width: number; height: number },
  after: { width: number; height: number },
): number {
  const a = before.height / before.width;
  const b = after.height / after.width;
  return Math.abs(b - a) / a;
}

export interface Drift {
  readonly view: AircraftView;
  readonly damages: number;
  readonly before: { width: number; height: number };
  readonly after: { width: number; height: number };
  /** Relative change of the aspect ratio, e.g. 0.083 for 8.3 %. */
  readonly change: number;
}

/**
 * The views whose pins would drift: views with damages whose effective
 * drawing changes its proportions by more than ASPECT_TOLERANCE. Empty means
 * the change is safe for every existing pin.
 */
export function drift(
  damagesPerView: Partial<Record<AircraftView, number>>,
  before: { rules: MirrorRules; sizeOf: SizeOf },
  after: { rules: MirrorRules; sizeOf: SizeOf },
): Drift[] {
  const out: Drift[] = [];
  for (const view of ALL_VIEWS) {
    const damages = damagesPerView[view] ?? 0;
    if (damages <= 0) continue;
    const b = effectiveSize(view, before.rules, before.sizeOf);
    const a = effectiveSize(view, after.rules, after.sizeOf);
    const change = aspectChange(b, a);
    if (change > ASPECT_TOLERANCE) out.push({ view, damages, before: b, after: a, change });
  }
  return out;
}

/** The person-readable reason for a refused change, naming the views and how many pins. */
export function driftMessage(drifts: readonly Drift[]): string {
  const parts = drifts.map(
    (d) =>
      `${d.view}: ${d.damages} ${d.damages === 1 ? 'Schaden' : 'Schäden'}, Seitenverhältnis ändert sich um ${(d.change * 100).toFixed(1).replace('.', ',')} %`,
  );
  return (
    'Die Pins vorhandener Schäden würden verrutschen (' +
    parts.join('; ') +
    '). Mit „Trotzdem übernehmen“ (force) wird die Änderung dennoch gespeichert.'
  );
}

/** Aircraft type codes (ck_aircraft_types_code): 2–16 characters A–Z, 0–9, '-', not starting with '-'. */
export const TYPE_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,15}$/;
export const TYPE_NAME_MIN = 2;
export const TYPE_NAME_MAX = 80;
export const TYPE_CREDIT_MIN = 3;
export const TYPE_CREDIT_MAX = 400;
/** sortOrder of an aircraft: 0 … 1 000 000; without one, behind the last (+10). */
export const SORT_ORDER_MAX = 1_000_000;
export const SORT_ORDER_STEP = 10;

export function isValidTypeCode(code: string): boolean {
  return TYPE_CODE_PATTERN.test(code);
}

/** Anonymised person (spec 15): e-mail and name become `geloescht-<16 hex>`. */
export const ANONYMIZED_PREFIX = 'geloescht-';

export function isAnonymizedLabel(value: string): boolean {
  return /^geloescht-[0-9a-f]{16}$/.test(value);
}
